import { useEffect, useMemo, useRef, useState } from 'react';
import { BringToFront, ChevronDown, Layers, SendToBack } from 'lucide-react';
import {
  useStore,
  useReadOnly,
  isCompNode,
  isAnnotationNode,
  isBlockNode,
  isMechanicalItemNode,
  selectActiveNodes,
  selectActiveEdges,
} from '../store';
import type {
  BlockShape,
  MechanicalMounting,
  MechanicalOrientation,
  Pin,
  PinKind,
  PinSide,
} from '../types';
import { NameModal } from './SheetModals';
import { ResizeHandle, loadPanelWidth } from './ResizeHandle';

const INSPECTOR_WIDTH_KEY = 'skema:inspector-width';
const INSPECTOR_MIN = 280;
const INSPECTOR_MAX = 620;

const PIN_KINDS: PinKind[] = ['power', 'gnd', 'signal', 'comm', 'analog', 'pwm', 'other'];
const PIN_SIDES: PinSide[] = ['left', 'right', 'top', 'bottom'];
const BLOCK_SHAPES: Array<{ value: BlockShape; label: string }> = [
  { value: 'rounded', label: 'Rounded box' },
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'pill', label: 'Pill' },
  { value: 'ellipse', label: 'Ellipse' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'hexagon', label: 'Hexagon' },
];
const MOUNTING_OPTIONS: MechanicalMounting[] = [
  'free',
  'din-rail',
  'panel',
  'door',
  'chassis',
  'bracket',
  'other',
];
const ORIENTATION_OPTIONS: Array<{ value: MechanicalOrientation; label: string }> = [
  { value: 'top', label: 'Top view (W x H)' },
  { value: 'side', label: 'Side mount (D x H)' },
  { value: 'front', label: 'Front mount (W x D)' },
];
type LayerMoveMode = 'front' | 'back' | 'forward' | 'backward';

export function Inspector() {
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const nodes = useStore(selectActiveNodes);
  const edges = useStore(selectActiveEdges);
  const node = nodes.find((n) => n.id === selectedNodeId);
  const edge = edges.find((e) => e.id === selectedEdgeId);
  const meta = useStore((s) => s.meta);

  const updateNodeData = useStore((s) => s.updateNodeData);
  const updatePin = useStore((s) => s.updatePin);
  const addPin = useStore((s) => s.addPin);
  const removePin = useStore((s) => s.removePin);
  const renameGroup = useStore((s) => s.renameGroup);
  const deleteNode = useStore((s) => s.deleteNode);
  const updateEdgeData = useStore((s) => s.updateEdgeData);
  const deleteEdge = useStore((s) => s.deleteEdge);
  const updateMeta = useStore((s) => s.updateMeta);
  const setAnnotationText = useStore((s) => s.setAnnotationText);
  const setAnnotationField = useStore((s) => s.setAnnotationField);
  const setBlockField = useStore((s) => s.setBlockField);
  const setMechanicalItemField = useStore((s) => s.setMechanicalItemField);
  const setMechanicalSheetScale = useStore((s) => s.setMechanicalSheetScale);
  const rotateNode = useStore((s) => s.rotateNode);
  const moveNodeLayer = useStore((s) => s.moveNodeLayer);
  const alignNodes = useStore((s) => s.alignNodes);
  const distributeNodes = useStore((s) => s.distributeNodes);
  const readOnly = useReadOnly();
  const selectedCount = nodes.filter((n) => n.selected).length;
  const multiSelectedEdges = edges.filter((e) => e.selected);
  const [width, setWidth] = useState<number>(() =>
    loadPanelWidth(INSPECTOR_WIDTH_KEY, 340, INSPECTOR_MIN, INSPECTOR_MAX),
  );
  useEffect(() => {
    try {
      localStorage.setItem(INSPECTOR_WIDTH_KEY, String(width));
    } catch {
      // ignore
    }
  }, [width]);

  return (
    <aside
      style={{
        width,
        background: 'var(--panel)',
        borderLeft: '1px solid var(--panel-border)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Read-only projects: dim the editable panel as a "disabled" cue. Edits
        // are already blocked in the store; this just signals it visually.
        opacity: readOnly ? 0.55 : 1,
      }}
    >
      <ResizeHandle edge="left" width={width} setWidth={setWidth} min={INSPECTOR_MIN} max={INSPECTOR_MAX} />
      <div
        style={{
          padding: '6px 12px',
          background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
          borderBottom: '1px solid var(--chrome-border)',
          fontWeight: 800,
          fontSize: 10.5,
          letterSpacing: 0.8,
          color: 'var(--text)',
          textTransform: 'uppercase',
          height: 28,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <div style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 3, background: 'var(--accent)' }} />
        Inspector
      </div>
      <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
        {selectedCount >= 2 && (
          <>
            <SectionTitle>{selectedCount} items selected</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 6 }}>
              <button style={alignBtn} onClick={() => alignNodes('left')} title="Align left">⫸ L</button>
              <button style={alignBtn} onClick={() => alignNodes('center-h')} title="Align horizontal center">↔ C</button>
              <button style={alignBtn} onClick={() => alignNodes('right')} title="Align right">R ⫷</button>
              <button style={alignBtn} onClick={() => alignNodes('top')} title="Align top">T ⫶</button>
              <button style={alignBtn} onClick={() => alignNodes('center-v')} title="Align vertical center">↕ M</button>
              <button style={alignBtn} onClick={() => alignNodes('bottom')} title="Align bottom">⫶ B</button>
            </div>
            {selectedCount >= 3 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4, marginTop: 2 }}>
                <button style={alignBtn} onClick={() => distributeNodes('h')} title="Distribute horizontally">
                  ⇔ Distribute H
                </button>
                <button style={alignBtn} onClick={() => distributeNodes('v')} title="Distribute vertically">
                  ⇕ Distribute V
                </button>
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
              Shift+click to add to selection, or drag a box on empty canvas.
            </div>
          </>
        )}

        {multiSelectedEdges.length >= 2 && (
          <BulkWirePanel selectedEdges={multiSelectedEdges} />
        )}

        {selectedCount < 2 && !node && !edge && multiSelectedEdges.length < 2 && (
          <>
            <SectionTitle>Project</SectionTitle>
            <Field label="Name">
              <input
                value={meta.name}
                onChange={(e) => updateMeta({ name: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Author">
              <input
                value={meta.author}
                onChange={(e) => updateMeta({ author: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Description">
              <textarea
                rows={5}
                value={meta.description}
                onChange={(e) => updateMeta({ description: e.target.value })}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </Field>
            <div
              style={{
                marginTop: 14,
                padding: 12,
                background: 'var(--bg-canvas)',
                border: '1px solid var(--panel-border)',
                borderRadius: 'var(--radius)',
                fontSize: 11,
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              Select a component or wire on the canvas to edit its properties.
            </div>
          </>
        )}

        {selectedCount < 2 && node && (
          <LayerControls nodeId={node.id} onMove={moveNodeLayer} />
        )}

        {selectedCount < 2 && node && isBlockNode(node) && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <SectionTitle>Block</SectionTitle>
              <button style={dangerBtnStyle} onClick={() => deleteNode(node.id)}>
                Delete
              </button>
            </div>
            <Field label="Title">
              <input
                value={node.data.title}
                onChange={(e) => setBlockField(node.id, { title: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Subtitle">
              <input
                value={node.data.subtitle ?? ''}
                onChange={(e) => setBlockField(node.id, { subtitle: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Color">
              <input
                type="color"
                value={node.data.color ?? '#1e40af'}
                onChange={(e) => setBlockField(node.id, { color: e.target.value })}
                style={{ ...inputStyle, padding: 2, height: 32 }}
              />
            </Field>
            <Field label="Text color">
              <input
                type="color"
                value={node.data.textColor ?? '#ffffff'}
                onChange={(e) => setBlockField(node.id, { textColor: e.target.value })}
                style={{ ...inputStyle, padding: 2, height: 32 }}
              />
            </Field>
            <Field label="Shape">
              <select
                value={node.data.shape ?? 'rounded'}
                onChange={(e) =>
                  setBlockField(node.id, { shape: e.target.value as BlockShape })
                }
                style={inputStyle}
              >
                {BLOCK_SHAPES.map((shape) => (
                  <option key={shape.value} value={shape.value}>
                    {shape.label}
                  </option>
                ))}
              </select>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <Field label="Width">
                <input
                  type="number"
                  value={node.data.width ?? 200}
                  onChange={(e) =>
                    setBlockField(node.id, { width: Number(e.target.value) || 200 })
                  }
                  style={inputStyle}
                />
              </Field>
              <Field label="Height">
                <input
                  type="number"
                  value={node.data.height ?? 96}
                  onChange={(e) =>
                    setBlockField(node.id, { height: Number(e.target.value) || 96 })
                  }
                  style={inputStyle}
                />
              </Field>
            </div>
          </>
        )}

        {selectedCount < 2 && node && isAnnotationNode(node) && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <SectionTitle>Note</SectionTitle>
              <button style={dangerBtnStyle} onClick={() => deleteNode(node.id)}>
                Delete
              </button>
            </div>
            <Field label="Text">
              <textarea
                rows={6}
                value={node.data.text}
                onChange={(e) => setAnnotationText(node.id, e.target.value)}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </Field>
            <Field label="Background color">
              <input
                type="color"
                value={node.data.color ?? '#fef3c7'}
                onChange={(e) => setAnnotationField(node.id, { color: e.target.value })}
                style={{ ...inputStyle, padding: 2, height: 32 }}
              />
            </Field>
          </>
        )}

        {selectedCount < 2 && node && isMechanicalItemNode(node) && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <SectionTitle>Mechanical Item</SectionTitle>
              <button style={dangerBtnStyle} onClick={() => deleteNode(node.id)}>
                Delete
              </button>
            </div>
            <Field label="Label">
              <input
                value={node.data.label}
                onChange={(e) => setMechanicalItemField(node.id, { label: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Part number">
              <input
                value={node.data.partNumber ?? ''}
                onChange={(e) =>
                  setMechanicalItemField(node.id, { partNumber: e.target.value })
                }
                style={inputStyle}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <Field label="Width (mm)">
                <input
                  type="number"
                  min={1}
                  step={0.1}
                  value={node.data.widthMm ?? 100}
                  onChange={(e) =>
                    setMechanicalItemField(node.id, { widthMm: Number(e.target.value) || 1 })
                  }
                  style={inputStyle}
                />
              </Field>
              <Field label="Height (mm)">
                <input
                  type="number"
                  min={1}
                  step={0.1}
                  value={node.data.heightMm ?? 60}
                  onChange={(e) =>
                    setMechanicalItemField(node.id, { heightMm: Number(e.target.value) || 1 })
                  }
                  style={inputStyle}
                />
              </Field>
              <Field label="Depth (mm)">
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  value={node.data.depthMm ?? ''}
                  onChange={(e) =>
                    setMechanicalItemField(node.id, {
                      depthMm: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  style={inputStyle}
                />
              </Field>
              <Field label="Sheet scale px/mm">
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={node.data.scalePxPerMm ?? 1}
                  onChange={(e) =>
                    setMechanicalSheetScale(Math.max(0.1, Number(e.target.value) || 1))
                  }
                  style={inputStyle}
                />
              </Field>
            </div>
            <Field label="Rotation">
              <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 32px', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => rotateNode(node.id, -90)}
                  style={secondaryBtn}
                  title="Rotate 90 degrees counter-clockwise"
                >
                  -90
                </button>
                <select
                  value={node.data.rotation ?? 0}
                  onChange={(e) =>
                    setMechanicalItemField(node.id, { rotation: Number(e.target.value) })
                  }
                  style={inputStyle}
                >
                  <option value={0}>0 deg</option>
                  <option value={90}>90 deg</option>
                  <option value={180}>180 deg</option>
                  <option value={270}>270 deg</option>
                </select>
                <button
                  type="button"
                  onClick={() => rotateNode(node.id, 90)}
                  style={secondaryBtn}
                  title="Rotate 90 degrees clockwise"
                >
                  +90
                </button>
              </div>
            </Field>
            <Field label="Mounting">
              <select
                value={node.data.mounting ?? 'free'}
                onChange={(e) =>
                  setMechanicalItemField(node.id, {
                    mounting: e.target.value as MechanicalMounting,
                  })
                }
                style={inputStyle}
              >
                {MOUNTING_OPTIONS.map((mounting) => (
                  <option key={mounting} value={mounting}>
                    {mounting}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Mount pose">
              <select
                value={node.data.orientation ?? 'top'}
                onChange={(e) =>
                  setMechanicalItemField(node.id, {
                    orientation: e.target.value as MechanicalOrientation,
                  })
                }
                style={inputStyle}
              >
                {ORIENTATION_OPTIONS.map((orientation) => (
                  <option key={orientation.value} value={orientation.value}>
                    {orientation.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Color">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="color"
                  value={node.data.color ?? '#64748b'}
                  onChange={(e) =>
                    setMechanicalItemField(node.id, { color: e.target.value, autoColor: false })
                  }
                  style={{ ...inputStyle, padding: 2, height: 32, flex: 1 }}
                  title="Pick a colour (overrides the auto colour for this item)"
                />
                <button
                  type="button"
                  onClick={() => setMechanicalItemField(node.id, { autoColor: true })}
                  disabled={node.data.autoColor !== false}
                  style={{
                    ...secondaryBtn,
                    opacity: node.data.autoColor !== false ? 0.5 : 1,
                  }}
                  title="Revert to the automatic colour-by-type"
                >
                  Auto
                </button>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
                {node.data.autoColor === false
                  ? 'Manual colour (overrides auto-by-type).'
                  : 'Auto colour by part type. Pick a colour to override.'}
              </div>
            </Field>
            <Field label="Notes">
              <textarea
                rows={4}
                value={node.data.notes ?? ''}
                onChange={(e) => setMechanicalItemField(node.id, { notes: e.target.value })}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </Field>
          </>
        )}

        {selectedCount < 2 && node && isCompNode(node) && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <SectionTitle>Component</SectionTitle>
              <button style={dangerBtnStyle} onClick={() => deleteNode(node.id)}>
                Delete
              </button>
            </div>
            <Field label="Label">
              <input
                value={node.data.label}
                onChange={(e) => updateNodeData(node.id, { label: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Category">
              <input
                value={node.data.category}
                onChange={(e) => updateNodeData(node.id, { category: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Part number">
              <input
                value={node.data.partNumber ?? ''}
                onChange={(e) => updateNodeData(node.id, { partNumber: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Manufacturer">
              <input
                value={node.data.manufacturer ?? ''}
                onChange={(e) => updateNodeData(node.id, { manufacturer: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Color">
              <input
                type="color"
                value={node.data.color ?? '#334155'}
                onChange={(e) => updateNodeData(node.id, { color: e.target.value })}
                style={{ ...inputStyle, padding: 2, height: 32 }}
              />
            </Field>

            <SectionTitle style={{ marginTop: 18 }}>Properties</SectionTitle>
            <PropertiesEditor
              properties={node.data.properties}
              onChange={(properties) => updateNodeData(node.id, { properties })}
            />

            <SectionTitle style={{ marginTop: 18 }}>Connectors</SectionTitle>
            <ConnectorTree
              pins={node.data.pins}
              onRenameGroup={(oldName, newName) => renameGroup(node.id, oldName, newName)}
              onUpdatePin={(pinId, patch) => updatePin(node.id, pinId, patch)}
              onRemovePin={(pinId) => removePin(node.id, pinId)}
              onAddPin={(group) =>
                addPin(node.id, {
                  name: `P${node.data.pins.length + 1}`,
                  side: 'left',
                  kind: 'other',
                  group: group || undefined,
                })
              }
            />
            <button
              style={addBtnStyle}
              onClick={() => {
                const name = prompt('New connector name (e.g. "CN1", "USB")', '');
                if (!name) return;
                addPin(node.id, {
                  name: `${name.replace(/\s+/g, '_').toUpperCase()}_1`,
                  side: 'left',
                  kind: 'other',
                  group: name.trim(),
                });
              }}
            >
              + Add connector
            </button>
          </>
        )}

        {selectedCount < 2 && edge && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <SectionTitle>Wire</SectionTitle>
              <button style={dangerBtnStyle} onClick={() => deleteEdge(edge.id)}>
                Delete
              </button>
            </div>

            <ConnectionSummary edge={edge} nodes={nodes} />

            {/* On the block diagram, a wire IS the harness. Its label is auto-
                derived from the harness name and we hide only the manual
                label / gauge fields — those are per-conductor things that
                only make sense in the schematic. */}
            {(() => {
              const srcN = nodes.find((n) => n.id === edge.source);
              const tgtN = nodes.find((n) => n.id === edge.target);
              const isBlockEdge =
                !!srcN && !!tgtN && isBlockNode(srcN) && isBlockNode(tgtN);
              if (isBlockEdge) {
                return (
                  <div
                    style={{
                      background: 'var(--panel-alt)',
                      border: '1px dashed var(--panel-border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '8px 10px',
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      lineHeight: 1.5,
                      marginBottom: 10,
                    }}
                  >
                    This is a <strong>block-diagram</strong> wire — it
                    represents an entire harness, not an individual conductor.
                    The label is auto-set to the harness name, and label /
                    gauge edits live on the schematic wires that the harness
                    contains. Route color and thickness can still be adjusted
                    below.
                  </div>
                );
              }
              return (
                <>
                  <Field label="Net name">
                    <input
                      value={edge.data?.netName ?? edge.data?.label ?? ''}
                      onChange={(e) =>
                        // One step: the net name is also the wire's label
                        // (shown on the wire, used by the harness / exports).
                        updateEdgeData(edge.id, {
                          netName: e.target.value || undefined,
                          label: e.target.value,
                        })
                      }
                      placeholder="e.g. RS485_A, MTR1_EN"
                      style={inputStyle}
                    />
                  </Field>
                  <Field label="Wire gauge (AWG)">
                    <input
                      value={edge.data?.gauge ?? ''}
                      onChange={(e) =>
                        updateEdgeData(edge.id, { gauge: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </Field>
                </>
              );
            })()}
            <Field label="Route color">
              <input
                value={edge.data?.color ?? ''}
                onChange={(e) =>
                  updateEdgeData(edge.id, { color: e.target.value })
                }
                placeholder="e.g. red, mavi, #2563eb"
                style={inputStyle}
              />
            </Field>
            <Field label="Color 2 (stripe)">
              <input
                value={edge.data?.color2 ?? ''}
                onChange={(e) =>
                  updateEdgeData(edge.id, { color2: e.target.value || undefined })
                }
                placeholder="e.g. white, beyaz — for two-tone wires"
                style={inputStyle}
              />
            </Field>
            <Field label="Line thickness">
              <input
                type="number"
                min={0.5}
                max={12}
                step={0.5}
                value={edge.data?.strokeWidth ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  updateEdgeData(edge.id, {
                    strokeWidth: raw === '' ? undefined : Number(raw),
                  });
                }}
                placeholder="default"
                style={inputStyle}
              />
            </Field>
            <HarnessAssignmentField edge={edge} />
            <Field label="Notes">
              <textarea
                rows={4}
                value={edge.data?.notes ?? ''}
                onChange={(e) => updateEdgeData(edge.id, { notes: e.target.value })}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </Field>
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * Harness assignment dropdown for a selected wire. Lists every bundle from
 * every interconnect sheet in the project and lets the user pick which
 * production harness this wire belongs to. Picking "+ New harness…" prompts
 * for a name and creates a fresh bundle in the first interconnect sheet —
 * this means the user never has to leave the schematic to start grouping
 * wires for production.
 */
/**
 * Heuristic: turn a set of selected wires into a starting seed for a new
 * harness's metadata. The user can edit afterwards — this just prefills the
 * obvious bits (which two units the harness runs between, and a per-gauge
 * roll-up of the cables it carries).
 */
function inferBundleSeedFromWires(
  wires: ReturnType<typeof selectActiveEdges>,
  nodes: ReturnType<typeof selectActiveNodes>,
): { fromUnit: string; toUnit: string; cables: string } {
  // Most-frequent source / target label. Both component nodes (schematic)
  // and block nodes (block diagram) contribute names so a harness created
  // from a block-diagram wire seeds with the block titles.
  const compById = new Map<string, string>();
  for (const n of nodes) {
    if (isCompNode(n)) compById.set(n.id, n.data.label || '?');
    else if (isBlockNode(n)) compById.set(n.id, n.data.title || '?');
  }
  const srcCount = new Map<string, number>();
  const tgtCount = new Map<string, number>();
  for (const w of wires) {
    const src = compById.get(w.source ?? '');
    const tgt = compById.get(w.target ?? '');
    if (src) srcCount.set(src, (srcCount.get(src) ?? 0) + 1);
    if (tgt) tgtCount.set(tgt, (tgtCount.get(tgt) ?? 0) + 1);
  }
  const topOf = (m: Map<string, number>) =>
    Array.from(m.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  const fromUnit = topOf(srcCount);
  const toUnit = topOf(tgtCount);

  // Cables roll-up: group by (gauge, color) and emit "N × <gauge> (<color>)"
  // lines. If gauge or color is missing we still emit a row so the user can
  // see and fill it in.
  const groups = new Map<string, number>();
  for (const w of wires) {
    const gauge = (w.data?.gauge ?? '').trim() || '—';
    const color = (w.data?.color ?? '').trim() || '—';
    const key = `${gauge}|${color}`;
    groups.set(key, (groups.get(key) ?? 0) + 1);
  }
  const cableLines: string[] = [];
  for (const [key, count] of Array.from(groups.entries()).sort(
    (a, b) => b[1] - a[1],
  )) {
    const [g, c] = key.split('|');
    const gaugeStr = g === '—' ? 'unspecified gauge' : g;
    const colorStr = c === '—' ? '' : ` (${c})`;
    cableLines.push(`${count} × ${gaugeStr}${colorStr}`);
  }
  return {
    fromUnit,
    toUnit,
    cables: cableLines.join('\n'),
  };
}

/**
 * Multi-wire bulk-edit panel. Active when the user has 2+ wires selected on
 * the canvas (Shift+click or drag-select). The main use is "assign every
 * selected wire to harness X in one click" so production cabling can be
 * grouped without picking each wire individually.
 */
function BulkWirePanel({
  selectedEdges,
}: {
  selectedEdges: ReturnType<typeof selectActiveEdges>;
}) {
  const sheets = useStore((s) => s.sheets);
  const bundles = useMemo(() => {
    const out: import('../types').InterconnectBundle[] = [];
    for (const sh of sheets) {
      if (sh.kind === 'interconnect' && sh.bundles) out.push(...sh.bundles);
    }
    return out;
  }, [sheets]);
  const assignBulk = useStore((s) => s.assignHarnessToEdges);
  const createHarnessForEdges = useStore((s) => s.createHarnessForEdges);
  const deleteEdge = useStore((s) => s.deleteEdge);
  const updateEdgeData = useStore((s) => s.updateEdgeData);

  // If every selected wire is on the same harness, surface that as the
  // current value; otherwise show "(mixed)".
  const harnessIds = new Set(selectedEdges.map((e) => e.data?.harnessId ?? ''));
  const commonHarness = harnessIds.size === 1 ? [...harnessIds][0] : null;

  const [pickedId, setPickedId] = useState<string>(commonHarness ?? '');
  const [bulkGauge, setBulkGauge] = useState('');
  const [bulkColor, setBulkColor] = useState('');
  const [bulkThickness, setBulkThickness] = useState('');
  const [, setNewHarnessOpen] = useState(false);

  const ids = selectedEdges.map((e) => e.id);
  const seed = { fromUnit: '', toUnit: '', cables: '' };
  const createHarnessFromSelection = (_name: string) => {
    createHarnessForEdges(ids);
  };

  return (
    <>
      <SectionTitle>{selectedEdges.length} wires selected</SectionTitle>

      <div
        style={{
          background: 'var(--panel-alt)',
          border: '1px solid var(--panel-border)',
          borderRadius: 'var(--radius-sm)',
          padding: 10,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'var(--text-muted)',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Assign harness to all
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            value={pickedId}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__new__') {
                const createdId = createHarnessForEdges(ids);
                if (createdId) setPickedId(createdId);
                return;
              }
              setPickedId(v);
            }}
            style={{ ...inputStyle, flex: 1 }}
          >
            <option value="">— None —</option>
            {bundles.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || '(unnamed)'}
                {b.fromUnit && b.toUnit ? ` · ${b.fromUnit} → ${b.toUnit}` : ''}
              </option>
            ))}
            <option value="__new__">+ New harness from selection…</option>
          </select>
          <button
            onClick={() => assignBulk(ids, pickedId || undefined)}
            style={primaryActionBtn}
            disabled={!pickedId && commonHarness === ''}
          >
            Apply
          </button>
        </div>
        {false && (
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-muted)',
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            Tip: choose “+ New harness from selection” — Skematiq will pre-fill
            From / To and a cables list from your wires.
          </div>
        )}
        {commonHarness === null && (
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-muted)',
              marginTop: 4,
            }}
          >
            Selected wires currently belong to <strong>{harnessIds.size}</strong> different
            harnesses (or none). Apply to unify.
          </div>
        )}
        {commonHarness !== null && commonHarness !== '' && (
          <button
            onClick={() => assignBulk(ids, undefined)}
            style={{ ...secondaryBtn, marginTop: 6 }}
          >
            Clear harness from all
          </button>
        )}
      </div>

      <div
        style={{
          background: 'var(--panel-alt)',
          border: '1px solid var(--panel-border)',
          borderRadius: 'var(--radius-sm)',
          padding: 10,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'var(--text-muted)',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Bulk attributes
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr auto', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Gauge</span>
          <input
            value={bulkGauge}
            onChange={(e) => setBulkGauge(e.target.value)}
            placeholder="e.g. 18 AWG"
            style={inputStyle}
          />
          <button
            onClick={() => {
              for (const id of ids) updateEdgeData(id, { gauge: bulkGauge });
            }}
            disabled={!bulkGauge.trim()}
            style={primaryActionBtn}
          >
            Apply
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Color</span>
          <input
            value={bulkColor}
            onChange={(e) => setBulkColor(e.target.value)}
            placeholder="e.g. red, mavi, #2563eb"
            style={inputStyle}
          />
          <button
            onClick={() => {
              for (const id of ids) updateEdgeData(id, { color: bulkColor });
            }}
            disabled={!bulkColor.trim()}
            style={primaryActionBtn}
          >
            Apply
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Width</span>
          <input
            type="number"
            min={0.5}
            max={12}
            step={0.5}
            value={bulkThickness}
            onChange={(e) => setBulkThickness(e.target.value)}
            placeholder="e.g. 2.5"
            style={inputStyle}
          />
          <button
            onClick={() => {
              for (const id of ids) {
                updateEdgeData(id, { strokeWidth: Number(bulkThickness) });
              }
            }}
            disabled={!bulkThickness.trim() || Number(bulkThickness) <= 0}
            style={primaryActionBtn}
          >
            Apply
          </button>
        </div>
      </div>

      <button
        onClick={() => {
          if (
            confirm(`Delete ${selectedEdges.length} selected wires? This cannot be undone.`)
          ) {
            for (const id of ids) deleteEdge(id);
          }
        }}
        style={{
          ...dangerBtnStyle,
          width: '100%',
          padding: '6px 10px',
          fontSize: 12,
          marginBottom: 10,
        }}
      >
        Delete all {selectedEdges.length} wires
      </button>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Shift+click on additional wires to extend the selection.
      </div>

      {false && (
        <NameModal
          title={`New harness from ${selectedEdges.length} selected wires`}
          description={
            'Skematiq will pre-fill the harness with what it can infer from your selection:\n' +
            (seed.fromUnit ? `  • From unit: ${seed.fromUnit}\n` : '') +
            (seed.toUnit ? `  • To unit: ${seed.toUnit}\n` : '') +
            (seed.cables
              ? `  • Cables in bundle:\n      ${seed.cables.replace(/\n/g, '\n      ')}\n`
              : '') +
            '\nYou can edit any of these on the Harness sheet afterwards.'
          }
          defaultValue="MOTOR_HARN_01"
          okLabel="Create harness"
          onCancel={() => setNewHarnessOpen(false)}
          onSubmit={(name) => {
            createHarnessFromSelection(name);
            setNewHarnessOpen(false);
          }}
        />
      )}
    </>
  );
}

function HarnessAssignmentField({
  edge,
}: {
  edge: ReturnType<typeof selectActiveEdges>[number];
}) {
  // Subscribe to sheets (stable ref) and derive bundles via useMemo —
  // see InterconnectSheet for why we can't use useStore(selectAllBundles).
  const sheets = useStore((s) => s.sheets);
  const bundles = useMemo(() => {
    const out: import('../types').InterconnectBundle[] = [];
    for (const sh of sheets) {
      if (sh.kind === 'interconnect' && sh.bundles) out.push(...sh.bundles);
    }
    return out;
  }, [sheets]);
  const updateEdgeData = useStore((s) => s.updateEdgeData);
  const focusedHarnessId = useStore((s) => s.focusedHarnessId);
  const focusHarness = useStore((s) => s.focusHarness);
  const createHarnessForEdges = useStore((s) => s.createHarnessForEdges);
  const addBundle = useStore((s) => s.addBundle);
  const setActiveSheet = useStore((s) => s.setActiveSheet);
  const addSheet = useStore((s) => s.addSheet);
  // Modal state for "+ New harness…" — Electron's window.prompt() returns
  // undefined silently, so we use a React modal instead.
  const [newHarnessOpen, setNewHarnessOpen] = useState(false);

  const currentId = edge.data?.harnessId ?? '';
  const currentBundle = bundles.find((b) => b.id === currentId);
  const isFocused = focusedHarnessId === currentId && currentId !== '';

  const createHarness = (name: string) => {
    if (!name.trim()) return;
    // Find the first interconnect sheet, or create one.
    let interconnectSheet = sheets.find((s) => s.kind === 'interconnect');
    if (!interconnectSheet) {
      addSheet('interconnect', 'Harnesses');
      const newSheets = useStore.getState().sheets;
      interconnectSheet = newSheets.find((s) => s.kind === 'interconnect');
      if (!interconnectSheet) return;
    }
    // Pre-fill from the wire's endpoints — same heuristic the multi-wire
    // panel uses but with a one-wire input.
    const allActiveNodes = useStore.getState().sheets.flatMap((s) => s.nodes);
    const seed = inferBundleSeedFromWires([edge], allActiveNodes);
    // Bundle CRUD operates on the *active* sheet — briefly switch over,
    // add the bundle, then switch back to where the user was so they don't
    // get yanked away from the schematic they're editing.
    const previousSheetId = useStore.getState().activeSheetId;
    setActiveSheet(interconnectSheet.id);
    addBundle({
      name: name.trim(),
      fromUnit: seed.fromUnit,
      toUnit: seed.toUnit,
      cables: seed.cables,
    });
    const created = (useStore.getState().sheets.find((s) => s.id === interconnectSheet!.id)
      ?.bundles ?? []) as { id: string; name: string }[];
    const newest = created[created.length - 1];
    setActiveSheet(previousSheetId);
    if (newest) updateEdgeData(edge.id, { harnessId: newest.id });
  };

  const onChange = (val: string) => {
    if (val === '__new__') {
      createHarnessForEdges([edge.id]);
      return;
    }
    updateEdgeData(edge.id, { harnessId: val || undefined });
  };

  return (
    <Field label="Harness (production)">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <select
          value={currentId}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, flex: 1 }}
        >
          <option value="">— None —</option>
          {bundles.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name || '(unnamed)'}
              {b.fromUnit && b.toUnit ? ` · ${b.fromUnit} → ${b.toUnit}` : ''}
            </option>
          ))}
          <option value="__new__">+ New harness…</option>
        </select>
        {currentBundle && (
          <button
            onClick={() => focusHarness(isFocused ? null : currentId)}
            title={
              isFocused
                ? 'Stop highlighting this harness'
                : 'Highlight every wire in this harness across the canvas'
            }
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '4px 8px',
              border: '1px solid var(--panel-border)',
              borderRadius: 3,
              background: isFocused ? '#2f81f7' : 'transparent',
              color: isFocused ? 'white' : 'var(--text-muted)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {isFocused ? '★ Focused' : 'Focus'}
          </button>
        )}
      </div>
      {currentBundle && (
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            marginTop: 4,
            lineHeight: 1.4,
          }}
        >
          {currentBundle.conduit && <>Conduit: {currentBundle.conduit}<br /></>}
          {currentBundle.length && <>Length: {currentBundle.length}</>}
        </div>
      )}
      {newHarnessOpen && (
        <NameModal
          title="Add new harness"
          description={
            'Creates a new cable bundle on the project’s Harness sheet and assigns this wire to it.\n\nExamples: "MOTOR_HARN_01", "Tether trunk", "Power loom".'
          }
          defaultValue="MOTOR_HARN_01"
          okLabel="Create harness"
          onCancel={() => setNewHarnessOpen(false)}
          onSubmit={(name) => {
            createHarness(name);
            setNewHarnessOpen(false);
          }}
        />
      )}
    </Field>
  );
}

/**
 * Compact "from → to" summary of a selected wire's endpoints.
 * Shows which component + which pin sit at each end. Designed to read like
 * a single-glance answer to "what does this wire connect?".
 */
function ConnectionSummary({
  edge,
  nodes,
}: {
  edge: import('@xyflow/react').Edge;
  nodes: ReturnType<typeof selectActiveNodes>;
}) {
  const srcNode = nodes.find((n) => n.id === edge.source);
  const tgtNode = nodes.find((n) => n.id === edge.target);
  const srcPin =
    srcNode && isCompNode(srcNode)
      ? srcNode.data.pins.find((p) => p.id === edge.sourceHandle)
      : undefined;
  const tgtPin =
    tgtNode && isCompNode(tgtNode)
      ? tgtNode.data.pins.find((p) => p.id === edge.targetHandle)
      : undefined;

  const nodeLabel = (n: typeof srcNode) => {
    if (!n) return '— Unknown —';
    if (isCompNode(n)) return n.data.label || n.data.partNumber || 'Component';
    if (isBlockNode(n)) return n.data.title || 'Block';
    return n.id;
  };
  const pinDisplay = (pin: Pin | undefined) => {
    if (!pin) return null;
    const groupSuffix = pin.group ? ` · ${pin.group}` : '';
    return `${pin.name}${groupSuffix}`;
  };

  return (
    <div
      style={{
        background: 'var(--panel-alt)',
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius-sm)',
        padding: '8px 10px',
        marginBottom: 10,
        fontSize: 11,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--text-muted)',
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Connection
      </div>
      {(() => {
        // Canonicalise direction: alphabetically earlier component is shown
        // as "from" so the same connector pair always renders the same way,
        // regardless of which end the user dragged the wire from.
        const aLabel = nodeLabel(srcNode);
        const bLabel = nodeLabel(tgtNode);
        const ac = aLabel.toLowerCase();
        const bc = bLabel.toLowerCase();
        const swap =
          ac !== bc
            ? ac > bc
            : (srcPin?.group ?? '').toLowerCase() >
              (tgtPin?.group ?? '').toLowerCase();
        const fromLabel = swap ? bLabel : aLabel;
        const fromPin = swap ? tgtPin : srcPin;
        const toLabel = swap ? aLabel : bLabel;
        const toPin = swap ? srcPin : tgtPin;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Endpoint
              role="From"
              nodeName={fromLabel}
              pin={pinDisplay(fromPin)}
              voltage={fromPin?.voltage}
              kind={fromPin?.kind}
            />
            <div
              style={{
                color: 'var(--text-muted)',
                fontFamily: 'ui-monospace, Consolas, monospace',
              }}
            >
              ↓
            </div>
            <Endpoint
              role="To"
              nodeName={toLabel}
              pin={pinDisplay(toPin)}
              voltage={toPin?.voltage}
              kind={toPin?.kind}
            />
          </div>
        );
      })()}
    </div>
  );
}

function Endpoint({
  role,
  nodeName,
  pin,
  voltage,
  kind,
}: {
  role: 'From' | 'To';
  nodeName: string;
  pin: string | null;
  voltage?: string;
  kind?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-muted)',
          fontSize: 10,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            background: role === 'From' ? '#dbeafe' : '#fef3c7',
            color: role === 'From' ? '#1e40af' : '#92400e',
            border: `1px solid ${role === 'From' ? '#93c5fd' : '#fde68a'}`,
            borderRadius: 3,
            padding: '0 4px',
            fontSize: 9,
          }}
        >
          {role.toUpperCase()}
        </span>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{nodeName}</span>
      </div>
      <div
        style={{
          fontFamily: 'ui-monospace, Consolas, monospace',
          fontSize: 11,
          color: 'var(--text)',
          paddingLeft: 38,
        }}
      >
        {pin ?? <span style={{ color: 'var(--text-muted)' }}>(no pin)</span>}
        {(voltage || kind) && (
          <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 10 }}>
            {voltage && <span>{voltage}</span>}
            {voltage && kind && <span> · </span>}
            {kind && <span>{kind}</span>}
          </span>
        )}
      </div>
    </div>
  );
}

function ConnectorTree({
  pins,
  onRenameGroup,
  onUpdatePin,
  onRemovePin,
  onAddPin,
}: {
  pins: Pin[];
  onRenameGroup: (oldName: string, newName: string) => void;
  onUpdatePin: (pinId: string, patch: Partial<Pin>) => void;
  onRemovePin: (pinId: string) => void;
  onAddPin: (group: string) => void;
}) {
  // Preserve group order as encountered in the pins array.
  const byGroup = new Map<string, Pin[]>();
  for (const p of pins) {
    const g = (p.group ?? '').trim();
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(p);
  }

  if (byGroup.size === 0) {
    return (
      <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '4px 2px' }}>
        No pins. Click <strong>+ Add connector</strong> to start.
      </div>
    );
  }

  const groupEntries = Array.from(byGroup.entries());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {groupEntries.map(([name, groupPins]) => (
        <ConnectorGroup
          key={name || '__ungrouped__'}
          name={name}
          pins={groupPins}
          onRenameGroup={onRenameGroup}
          onUpdatePin={onUpdatePin}
          onRemovePin={onRemovePin}
          onAddPin={() => onAddPin(name)}
        />
      ))}
    </div>
  );
}

function ConnectorGroup({
  name,
  pins,
  onRenameGroup,
  onUpdatePin,
  onRemovePin,
  onAddPin,
}: {
  name: string;
  pins: Pin[];
  onRenameGroup: (oldName: string, newName: string) => void;
  onUpdatePin: (pinId: string, patch: Partial<Pin>) => void;
  onRemovePin: (pinId: string) => void;
  onAddPin: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const long = name.length > 14;
  const isUngrouped = !name;

  return (
    <div
      style={{
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius)',
        background: expanded ? 'var(--panel-alt)' : 'var(--panel)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          padding: 4,
        }}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: 12,
            fontWeight: 700,
            width: 20,
            height: 24,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? '▾' : '▸'}
        </button>
        {isUngrouped ? (
          <div
            style={{
              flex: 1,
              fontSize: 11,
              color: 'var(--text-muted)',
              fontStyle: 'italic',
              padding: '0 4px',
            }}
          >
            Ungrouped
          </div>
        ) : (
          <input
            value={name}
            onChange={(e) => onRenameGroup(name, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              ...inputStyle,
              flex: 1,
              borderColor: long ? '#fbbf24' : 'var(--panel-border)',
              fontWeight: 600,
            }}
            title={
              long
                ? 'Long connector name — shorten to ≤ 14 chars for best readability'
                : undefined
            }
          />
        )}
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-dim)',
            whiteSpace: 'nowrap',
            padding: '0 6px 0 2px',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          {pins.length} pin{pins.length === 1 ? '' : 's'}
        </div>
      </div>
      {expanded && (
        <div
          style={{
            padding: '2px 8px 8px 26px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            borderTop: '1px solid var(--panel-border)',
          }}
        >
          {pins.map((pin) => (
            <PinRow
              key={pin.id}
              pin={pin}
              onChange={(patch) => onUpdatePin(pin.id, patch)}
              onRemove={() => onRemovePin(pin.id)}
            />
          ))}
          <button style={addBtnStyle} onClick={onAddPin}>
            + Add pin to {name || '(ungrouped)'}
          </button>
        </div>
      )}
    </div>
  );
}

function PropertiesEditor({
  properties,
  onChange,
}: {
  properties: Record<string, string>;
  onChange: (p: Record<string, string>) => void;
}) {
  const entries = Object.entries(properties);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            value={k}
            onChange={(e) => {
              const newKey = e.target.value;
              if (newKey === k) return;
              const copy: Record<string, string> = {};
              for (const [ek, ev] of entries) copy[ek === k ? newKey : ek] = ev;
              onChange(copy);
            }}
            style={{ ...inputStyle, flex: 1 }}
          />
          <input
            value={v}
            onChange={(e) => onChange({ ...properties, [k]: e.target.value })}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            style={removeBtnStyle}
            onClick={() => {
              const copy = { ...properties };
              delete copy[k];
              onChange(copy);
            }}
          >
            ×
          </button>
        </div>
      ))}
      <button
        style={addBtnStyle}
        onClick={() => {
          let i = entries.length + 1;
          let key = `prop${i}`;
          while (key in properties) {
            i += 1;
            key = `prop${i}`;
          }
          onChange({ ...properties, [key]: '' });
        }}
      >
        + Add property
      </button>
    </div>
  );
}

function PinRow({
  pin,
  onChange,
  onRemove,
}: {
  pin: Pin;
  onChange: (patch: Partial<Pin>) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        background: 'var(--panel-alt)',
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius)',
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
        <input
          placeholder="Name"
          value={pin.name}
          onChange={(e) => onChange({ name: e.target.value })}
          style={inputStyle}
        />
        <select
          value={pin.side}
          onChange={(e) => onChange({ side: e.target.value as PinSide })}
          style={inputStyle}
        >
          {PIN_SIDES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button style={removeBtnStyle} onClick={onRemove}>
          ×
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <select
          value={pin.kind}
          onChange={(e) => onChange({ kind: e.target.value as PinKind })}
          style={inputStyle}
        >
          {PIN_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input
          placeholder="Voltage"
          value={pin.voltage ?? ''}
          onChange={(e) => onChange({ voltage: e.target.value })}
          style={inputStyle}
        />
      </div>
      <input
        placeholder="Group / connector"
        value={pin.group ?? ''}
        onChange={(e) => onChange({ group: e.target.value })}
        style={inputStyle}
      />
      <input
        placeholder="Notes"
        value={pin.notes ?? ''}
        onChange={(e) => onChange({ notes: e.target.value })}
        style={inputStyle}
      />
    </div>
  );
}

function SectionTitle({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.8,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        padding: '6px 0 8px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function LayerControls({
  nodeId,
  onMove,
}: {
  nodeId: string;
  onMove: (id: string, mode: LayerMoveMode) => void;
}) {
  const [openMenu, setOpenMenu] = useState<'bring' | 'send' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openMenu) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };
    window.addEventListener('mousedown', closeOnOutsideClick);
    return () => window.removeEventListener('mousedown', closeOnOutsideClick);
  }, [openMenu]);

  const run = (mode: LayerMoveMode) => {
    onMove(nodeId, mode);
    setOpenMenu(null);
  };

  return (
    <div
      ref={rootRef}
      style={{
        marginBottom: 12,
        padding: '8px 10px',
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius)',
        background: 'var(--panel-alt)',
      }}
    >
      <SectionTitle style={{ paddingTop: 0 }}>Arrange</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <LayerSplitButton
          label="Bring to Front"
          title="Move selected item above all other items"
          icon={<BringToFront size={15} />}
          open={openMenu === 'bring'}
          onPrimary={() => run('front')}
          onToggle={() => setOpenMenu(openMenu === 'bring' ? null : 'bring')}
          items={[
            {
              label: 'Bring Forward',
              description: 'Move one layer forward',
              onClick: () => run('forward'),
            },
            {
              label: 'Bring to Front',
              description: 'Move above all items',
              onClick: () => run('front'),
            },
          ]}
        />
        <LayerSplitButton
          label="Send to Back"
          title="Move selected item behind all other items"
          icon={<SendToBack size={15} />}
          open={openMenu === 'send'}
          onPrimary={() => run('back')}
          onToggle={() => setOpenMenu(openMenu === 'send' ? null : 'send')}
          items={[
            {
              label: 'Send Backward',
              description: 'Move one layer backward',
              onClick: () => run('backward'),
            },
            {
              label: 'Send to Back',
              description: 'Move behind all items',
              onClick: () => run('back'),
            },
          ]}
        />
      </div>
    </div>
  );
}

function LayerSplitButton({
  label,
  title,
  icon,
  open,
  onPrimary,
  onToggle,
  items,
}: {
  label: string;
  title: string;
  icon: React.ReactNode;
  open: boolean;
  onPrimary: () => void;
  onToggle: () => void;
  items: Array<{ label: string; description: string; onClick: () => void }>;
}) {
  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <div style={{ display: 'flex', minWidth: 0 }}>
        <button
          title={title}
          onClick={onPrimary}
          style={{
            ...arrangePrimaryBtn,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none',
            flex: 1,
            minWidth: 0,
          }}
        >
          <span style={arrangeIconBox}>{icon}</span>
          <span style={arrangeLabel}>{label}</span>
        </button>
        <button
          title={`${label} options`}
          aria-label={`${label} options`}
          onClick={onToggle}
          style={{
            ...arrangeArrowBtn,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
        >
          <ChevronDown
            size={14}
            style={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.12s ease',
            }}
          />
        </button>
      </div>
      {open && (
        <div style={arrangeMenu}>
          {items.map((item) => (
            <button
              key={item.label}
              onClick={item.onClick}
              style={arrangeMenuItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent-soft)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Layers size={14} />
              <span style={{ minWidth: 0 }}>
                <span style={arrangeMenuLabel}>{item.label}</span>
                <span style={arrangeMenuDescription}>{item.description}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          marginBottom: 4,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 8px',
  fontSize: 12,
  boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const addBtnStyle: React.CSSProperties = {
  marginTop: 8,
  padding: '6px 10px',
  background: 'var(--panel)',
  color: 'var(--accent)',
  border: '1px dashed var(--panel-border-strong)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 500,
  width: '100%',
};

const removeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--danger)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  width: 28,
  height: 28,
  fontSize: 14,
  padding: 0,
};

const dangerBtnStyle: React.CSSProperties = {
  background: 'var(--danger-soft)',
  color: 'var(--danger)',
  border: '1px solid #fecaca',
  borderRadius: 'var(--radius-sm)',
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 500,
};

const alignBtn: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 4px',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 500,
};

const arrangePrimaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  minHeight: 34,
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 7px',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  textAlign: 'left',
};

const arrangeArrowBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  minHeight: 34,
  flexShrink: 0,
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 0,
  cursor: 'pointer',
};

const arrangeIconBox: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  flexShrink: 0,
  color: 'var(--text-muted)',
};

const arrangeLabel: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const arrangeMenu: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  minWidth: 188,
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  boxShadow: 'var(--shadow-lg)',
  padding: '4px 0',
  zIndex: 30,
};

const arrangeMenuItem: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  background: 'transparent',
  color: 'var(--text)',
  border: 'none',
  padding: '7px 10px',
  cursor: 'pointer',
  textAlign: 'left',
};

const arrangeMenuLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
};

const arrangeMenuDescription: React.CSSProperties = {
  display: 'block',
  marginTop: 2,
  fontSize: 10,
  color: 'var(--text-muted)',
  lineHeight: 1.2,
};

const primaryActionBtn: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 10px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: 11,
  cursor: 'pointer',
};
